/**
 * VistaPreviaCapacidad — Simulación de capacidad para una fecha.
 *
 * Muestra los slots de atención en formato timeline estilo Outlook: eje de
 * horas a la izquierda, bloques horizontales coloreados por estado.
 *
 * Subcolección de lectura: `salas/{salaId}/slots`.
 * CFs: portal_regenerarSlotsDia, portal_setCapacidadOverride, portal_migrarSalaAV2.
 */
import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  BarChart3,
  RefreshCw,
  Sliders,
  Info,
  AlertTriangle,
  Users,
  Clock,
  Calendar,
  User,
} from 'lucide-react';
import { db, functions } from '../../config/firebase';
import { useToast } from '../../contexts/ToastContext';
import { Input } from '../ui/Input';
import styles from './VistaPreviaCapacidad.module.css';

interface Slot {
  id: string;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  capacidadPlanificada: number;
  capacidadDinamica: number;
  capacidadOverride: number | null;
  capacidadEfectiva: number;
  cuposOcupados: number;
  cuposDisponibles: number;
  estado: string;
  medicosAsignados: string[];
}

interface DiagnosticInfo {
  tieneHorarios: boolean;
  tieneMedicos: boolean;
  checked: boolean;
}

const callRegenerar = httpsCallable(functions, 'portal_regenerarSlotsDia');
const callSetOverride = httpsCallable(functions, 'portal_setCapacidadOverride');
const callMigrarV2 = httpsCallable(functions, 'portal_migrarSalaAV2');

export interface VistaPreviaCapacidadProps {
  salaId: string;
}

function panamaToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Panama',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}


export function VistaPreviaCapacidad({ salaId }: VistaPreviaCapacidadProps) {
  const { addToast } = useToast();
  const [fecha, setFecha] = useState(panamaToday());
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [regenerando, setRegenerando] = useState(false);
  const [overrideVal, setOverrideVal] = useState<string>('');
  const [diagnostic, setDiagnostic] = useState<DiagnosticInfo>({
    tieneHorarios: false,
    tieneMedicos: false,
    checked: false,
  });

  // ── Diagnóstico rápido: verificar pre-condiciones ──
  useEffect(() => {
    if (!salaId) return;
    let cancelled = false;

    const horariosRef = collection(db, `salas/${salaId}/horarios`);
    const medicosRef = collection(db, `salas/${salaId}/medicos_sala`);

    Promise.all([
      new Promise<boolean>((resolve) => {
        const unsub = onSnapshot(
          query(horariosRef),
          (snap) => { unsub(); resolve(!snap.empty); },
          () => { unsub(); resolve(false); },
        );
      }),
      new Promise<boolean>((resolve) => {
        const unsub = onSnapshot(
          query(medicosRef),
          (snap) => { unsub(); resolve(!snap.empty); },
          () => { unsub(); resolve(false); },
        );
      }),
    ]).then(([h, m]) => {
      if (!cancelled) {
        setDiagnostic({ tieneHorarios: h, tieneMedicos: m, checked: true });
      }
    }).catch(() => {
      if (!cancelled) {
        setDiagnostic({ tieneHorarios: false, tieneMedicos: false, checked: true });
      }
    });

    return () => { cancelled = true; };
  }, [salaId]);

  // ── Suscripción a slots ──
  useEffect(() => {
    if (!salaId) return;
    const q = query(
      collection(db, `salas/${salaId}/slots`),
      where('fecha', '==', fecha),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items: Slot[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Slot, 'id'>),
        }));
        items.sort((a, b) => a.horaInicio.localeCompare(b.horaInicio));
        setSlots(items);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [salaId, fecha]);

  // ── Regenerar ──
  const handleRegenerar = async () => {
    setRegenerando(true);
    try {
      let res;
      try {
        res = await callRegenerar({ salaId, fecha });
      } catch (err: unknown) {
        const msg =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err && 'message' in err
              ? String((err as { message: unknown }).message)
              : '';
        if (msg.includes('usaHorariosV2') || msg.includes('modelo V2')) {
          addToast('info', 'Activando modelo de horarios V2…');
          await callMigrarV2({ salaId, activarV2: true });
          res = await callRegenerar({ salaId, fecha });
        } else {
          throw err;
        }
      }
      const data = res.data as { slotsGenerados: number; cuposTotalDia: number };
      if (data.slotsGenerados === 0) {
        addToast(
          'warning',
          'No se generaron slots. Verifique que la sala tenga horarios configurados y médicos asignados para esta fecha.',
        );
      } else {
        addToast(
          'success',
          `${data.slotsGenerados} slot(s) generados · ${data.cuposTotalDia} cupo(s) totales`,
        );
      }
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error al regenerar');
    } finally {
      setRegenerando(false);
    }
  };

  // ── Override ──
  const handleSetOverride = async () => {
    const num = overrideVal.trim() === '' ? null : Number(overrideVal);
    if (overrideVal.trim() !== '' && (isNaN(num as number) || (num as number) < 0)) {
      addToast('error', 'Valor inválido');
      return;
    }
    try {
      await callSetOverride({ salaId, cuposPorHora: num });
      addToast('success', num === null ? 'Override eliminado' : `Override = ${num}`);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error');
    }
  };

  const totales = useMemo(() => {
    const cupos = slots.reduce((s, x) => s + x.capacidadEfectiva, 0);
    const ocupados = slots.reduce((s, x) => s + x.cuposOcupados, 0);
    const disponibles = slots.reduce((s, x) => s + x.cuposDisponibles, 0);
    const medicos = new Set<string>();
    slots.forEach((s) => s.medicosAsignados.forEach((m) => medicos.add(m)));
    return { cupos, ocupados, disponibles, medicos: medicos.size };
  }, [slots]);

  const missingPrereqs = diagnostic.checked && (!diagnostic.tieneHorarios || !diagnostic.tieneMedicos);

  const estadoLabel: Record<string, string> = {
    abierto: 'Disponible',
    cerrado: 'Cerrado',
    lleno: 'Lleno',
    en_riesgo: 'En riesgo',
    suspendido: 'Suspendido',
  };

  return (
    <div className={styles.preview}>
      {/* ── Explicación ── */}
      <div className={styles.infoBanner}>
        <Info size={16} className={styles.infoIcon} />
        <div>
          <strong>Vista previa de capacidad</strong> — Simula cuántos pacientes
          puede atender la sala en una fecha, combinando horarios, médicos
          asignados y suspensiones. Presione <strong>Regenerar</strong> tras
          modificar la configuración para verificar el resultado. Use el{' '}
          <strong>tope manual</strong> para limitar cupos/hora temporalmente sin
          tocar horarios.
        </div>
      </div>

      {/* ── Alertas de pre-condiciones ── */}
      {missingPrereqs && (
        <div className={styles.warningBanner}>
          <AlertTriangle size={16} />
          <div>
            <strong>Configuración incompleta</strong> — Para generar slots se
            necesitan al menos:
            <ul className={styles.warningList}>
              <li className={diagnostic.tieneHorarios ? styles.checkOk : styles.checkMissing}>
                <Clock size={13} />
                Horarios recurrentes{' '}
                {diagnostic.tieneHorarios ? '(configurados)' : '(sin configurar)'}
              </li>
              <li className={diagnostic.tieneMedicos ? styles.checkOk : styles.checkMissing}>
                <Users size={13} />
                Médicos asignados{' '}
                {diagnostic.tieneMedicos ? '(asignados)' : '(ninguno asignado)'}
              </li>
            </ul>
            Complete la configuración en las pestañas correspondientes y vuelva
            aquí para verificar.
          </div>
        </div>
      )}

      {/* ── Controles ── */}
      <div className={styles.header}>
        <div className={styles.controls}>
          <div className={styles.dateControl}>
            <Calendar size={14} />
            <Input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>
          <button
            type="button"
            className={styles.btnRefresh}
            onClick={handleRegenerar}
            disabled={regenerando}
            title="Calcular slots para esta fecha con la configuración actual"
          >
            <RefreshCw size={14} className={regenerando ? styles.spinning : ''} />
            {regenerando ? 'Calculando…' : 'Regenerar'}
          </button>
        </div>
      </div>

      {/* ── KPIs ── */}
      {slots.length > 0 && (
        <div className={styles.kpis}>
          <div className={styles.kpi}>
            <span className={styles.kpiVal}>{totales.cupos}</span>
            <span className={styles.kpiLabel}>cupos totales</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiVal}>{totales.disponibles}</span>
            <span className={styles.kpiLabel}>disponibles</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiVal}>{totales.ocupados}</span>
            <span className={styles.kpiLabel}>ocupados</span>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiVal}>{totales.medicos}</span>
            <span className={styles.kpiLabel}>médicos del día</span>
          </div>
        </div>
      )}

      {/* ── Override manual ── */}
      {slots.length > 0 && (
        <div className={styles.overrideRow}>
          <span className={styles.overrideLabel}>
            <Sliders size={14} /> Tope manual de cupos/hora:
          </span>
          <input
            type="number"
            min={0}
            className={styles.overrideInput}
            value={overrideVal}
            onChange={(e) => setOverrideVal(e.target.value)}
            placeholder="(sin tope)"
          />
          <button
            type="button"
            className={styles.btnRefresh}
            onClick={handleSetOverride}
          >
            Aplicar
          </button>
        </div>
      )}

      {/* ── Timeline de slots ── */}
      <div className={styles.slotsWrap}>
        {loading ? (
          <p className={styles.empty}>Cargando…</p>
        ) : slots.length === 0 ? (
          <div className={styles.emptyState}>
            <BarChart3 size={32} className={styles.emptyIcon} />
            <p className={styles.emptyTitle}>Sin slots para {fecha}</p>
            <p className={styles.emptyText}>
              {missingPrereqs
                ? 'Complete la configuración de horarios y médicos, luego presione "Regenerar".'
                : 'Presione "Regenerar" para calcular los slots con la configuración actual de horarios y médicos.'}
            </p>
          </div>
        ) : (
          <>
            {/* Legend */}
            <div className={styles.legend}>
              <span className={styles.legendHint}>
                Cada fila es una franja de consulta. Los cupos se calculan según
                la cantidad de médicos que cubren esa franja (1 médico = 1 cupo).
              </span>
              <div className={styles.legendItems}>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendDot} ${styles.legendOpen}`} /> Disponible
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendDot} ${styles.legendFull}`} /> Lleno
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendDot} ${styles.legendClosed}`} /> Cerrado
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendDot} ${styles.legendSuspended}`} /> Suspendido
                </span>
              </div>
            </div>

            {/* Table */}
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thHora}>Hora</th>
                    <th className={styles.thEstado}>Estado</th>
                    <th className={styles.thDisp}>Disponibilidad</th>
                    <th className={styles.thMed}>Médicos</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.map((s) => {
                    const estado = s.estado || 'abierto';
                    return (
                      <tr key={s.id} className={styles[`row_${estado}`] ?? ''}>
                        <td className={styles.tdHora}>
                          {s.horaInicio} – {s.horaFin}
                        </td>
                        <td className={styles.tdEstado}>
                          <span className={`${styles.estadoBadge} ${styles[`badge_${estado}`] ?? ''}`}>
                            {estadoLabel[estado] ?? estado}
                          </span>
                        </td>
                        <td className={styles.tdDisp}>
                          <span className={styles.dispNum}>{s.cuposDisponibles}</span>
                          <span className={styles.dispSep}>/</span>
                          <span className={styles.dispTotal}>{s.capacidadEfectiva}</span>
                        </td>
                        <td className={styles.tdMed}>
                          <User size={12} className={styles.medIcon} />
                          {s.medicosAsignados.length}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
